import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Box, // Added Box for loading indicator centering
  Typography, // Added Typography for potential error display
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook"; // Import notification hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const NewCourseRequest = ({ schoolId, pathwayEmail, opEmail }) => {
  // Use Firestore hook to add data to 'NewCourseRequests' collection (Corrected name)
  const { addData: addFirestoreData } = useFirestore("NewCourseRequests"); // Ensure this matches your Firestore collection name
  // Use notification hook
  const {
    sendNotification,
    loading: notificationLoading,
    error: notificationError,
  } = useSendSchoolNotification();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Combined loading state for form submission
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // 'success' or 'error'

  const initialFormData = {
    courseRequest: "",
    startDate: "",
    endDate: "",
    modality: "",
    instructorType: "",
    instructorName: "",
    startTime: "",
    endTime: "",
    daysOfWeek: "",
    schoolName: "",
    fromName: "",
    notes: "", // Added notes field
    schoolId: schoolId || "", // Ensure initial value is set
    pathwayEmail: pathwayEmail || "", // Ensure initial value is set
    opEmail: opEmail || "", // Ensure initial value is set
    // Date_Submitted will be added dynamically on submit
  };

  const [formData, setFormData] = useState(initialFormData);

  // Update formData if props change
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      schoolId: schoolId || "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    }));
  }, [schoolId, pathwayEmail, opEmail]); // Add schoolId to dependency array

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation example
    if (!formData.instructorType) {
      setSnackbarMessage("Instructor Type is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.courseRequest) {
      setSnackbarMessage("Course Request is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.fromName || formData.fromName.trim() === "") {
      setSnackbarMessage("Your Name (Submitter) is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.schoolName || formData.schoolName.trim() === "") {
      setSnackbarMessage("School Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Add more validation as needed

    setLoading(true);
    setSnackbarMessage(""); // Clear previous messages

    const emailBodyContent = [
      `<strong>School Name:</strong> ${formData.schoolName || "N/A"}`,
      `<strong>Submitter Name:</strong> ${formData.fromName || "N/A"}`,
      `<strong>Course Request:</strong> ${formData.courseRequest || "N/A"}`,
      `<strong>Instructor Type:</strong> ${formData.instructorType || "N/A"}`,
      `<strong>Instructor Name:</strong> ${formData.instructorName || "N/A"}`,
      `<strong>Modality:</strong> ${formData.modality || "N/A"}`,
      `<strong>Start Date:</strong> ${formData.startDate || "N/A"}`,
      `<strong>End Date:</strong> ${formData.endDate || "N/A"}`,
      `<strong>Days of Week:</strong> ${formData.daysOfWeek || "N/A"}`,
      `<strong>Start Time:</strong> ${formData.startTime || "N/A"}`,
      `<strong>End Time:</strong> ${formData.endTime || "N/A"}`,
      `<strong>Notes:</strong> ${formData.notes || "N/A"}`,
    ].join("<br />");

    // Prepare data for Firestore, including the timestamp
    const dataForFirestore = {
      ...formData,
      Date_Submitted: Timestamp.now(), // Add the current timestamp here
    };

    // Prepare data for the notification
    // We are now constructing a specific payload for the notification.
    // The emailBodyContent will be the primary source for the email's body.
    // Other fields are included if the backend API needs them for routing,
    // 'To'/'From' headers, logging, or other metadata, but NOT for reconstructing the email body.
    const dataForNotification = {
      // Essential routing and identification information
      pathwayEmail:
        formData.pathwayEmail || pathwayEmail || "ccplus@sinclair.edu",
      opEmail: formData.opEmail || opEmail || "ccplus@sinclair.edu",
      schoolId: formData.schoolId || schoolId || "1",
      fromName: formData.fromName.trim(), // For sender identification or 'From' field
      schoolName: formData.schoolName.trim(), // For context

      // The pre-formatted email body
      emailBodyContent: emailBodyContent, // Add the pre-formatted string
    };

    console.log("Form data:", formData);
    console.log("Notification data:", dataForNotification);

    try {
      // 1. Add data to Firestore
      const docRef = await addFirestoreData(dataForFirestore); // Use the prepared data

      // 2. Send notification email
      // Assuming 'NewCourseRequest' is a suitable identifier for the API/template
      await sendNotification(
        dataForNotification,
        "New Course Request",
        "NewCourseRequest"
      );
      console.log("Notification sent successfully.");

      // 3. Show success message and close
      setSnackbarMessage("Request submitted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose(); // Close dialog and reset form
    } catch (error) {
      console.error("Error submitting form:", error);
      // Determine if the error came from Firestore or Notification sending
      const message = error.message || "An error occurred during submission.";
      setSnackbarMessage(`Error: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false); // Ensure loading is turned off
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false); // Reset loading state on close
    // Reset form, keeping essential props like emails and schoolId
    setFormData({
      ...initialFormData, // Spread initial state
      schoolId: schoolId || "", // Keep current prop value
      pathwayEmail: pathwayEmail || "", // Keep current prop value
      opEmail: opEmail || "", // Keep current prop value
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleClickOpen}
        sx={{ color: "white" }}
      >
        New Course Request
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          New Course Request
        </ModalDialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* --- Form Fields --- */}
              <TextField
                margin="dense"
                label="Course Request"
                name="courseRequest"
                fullWidth
                required // Make required if necessary
                value={formData.courseRequest}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Start Date"
                type="date"
                name="startDate"
                fullWidth
                value={formData.startDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="dense"
                label="End Date"
                type="date"
                name="endDate"
                fullWidth
                value={formData.endDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Modality</InputLabel>
                <Select
                  name="modality"
                  value={formData.modality}
                  onChange={handleChange}
                  label="Modality" // Add label for accessibility
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Face to Face">Face to Face</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Remote">Remote</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="dense" required>
                {" "}
                {/* Make required */}
                <InputLabel>Instructor Type</InputLabel>
                <Select
                  name="instructorType"
                  value={formData.instructorType}
                  onChange={handleChange}
                  label="Instructor Type" // Add label for accessibility
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Sinclair">Sinclair</MenuItem>
                  <MenuItem value="High School">High School</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label="Instructor Name"
                name="instructorName"
                fullWidth
                value={formData.instructorName}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Start Time"
                name="startTime"
                type="time" // Use time type if appropriate
                fullWidth
                value={formData.startTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                margin="dense"
                label="End Time"
                name="endTime"
                type="time" // Use time type if appropriate
                fullWidth
                value={formData.endTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                margin="dense"
                label="Days of the Week (e.g., MWF, TR)"
                name="daysOfWeek"
                fullWidth
                value={formData.daysOfWeek}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="School Name"
                name="schoolName"
                fullWidth
                required
                value={formData.schoolName}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Your Name (Submitter)" // Clarified label
                name="fromName"
                fullWidth
                required // Make required if necessary
                value={formData.fromName}
                onChange={handleChange}
              />
              <TextField // Added Notes field
                margin="dense"
                label="Notes"
                name="notes"
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange}
              />
              {/* Hidden fields for context - consider if they should be visible/editable */}
              {/* <TextField margin="dense" label="School ID" name="schoolId" fullWidth value={formData.schoolId} disabled /> */}
              {/* <TextField margin="dense" label="Pathway Email" name="pathwayEmail" fullWidth value={formData.pathwayEmail} disabled /> */}
              {/* <TextField margin="dense" label="OP Email" name="opEmail" fullWidth value={formData.opEmail} disabled /> */}
            </form>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Submit" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }} // Ensure alert takes full width
          variant="filled" // Use filled variant for better visibility
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewCourseRequest;
